package com.dotdrives.web.pages.nextsteps

import com.dotdrives.design.DotTone
import com.dotdrives.model.AutomationRule
import com.dotdrives.model.DriveHealthEntry
import com.dotdrives.model.ReminderSummary
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.intOrNull
import kotlinx.serialization.json.jsonPrimitive
import kotlin.time.Instant

enum class DueBucket { Overdue, ThisWeek }

data class ReminderGroup(
    val bucket: DueBucket,
    val label: String,
    val reminders: List<ReminderSummary>,
)

/**
 * "OVERDUE" / "THIS WEEK" grouping (`portal-next-steps.dc.html`). A reminder is overdue when
 * its due date falls before the start of today, regardless of time of day -- a reminder due
 * this morning is not "overdue" at 9am. Only open reminders (`completedAt == null`) are
 * grouped; the `Done` filter's list is rendered flat by the caller.
 */
fun groupReminders(reminders: List<ReminderSummary>, now: Instant): List<ReminderGroup> {
    val open = reminders.filter { it.completedAt == null }
    val (overdue, upcoming) = open.partition { isOverdue(it.dueAt, now) }
    return buildList {
        if (overdue.isNotEmpty()) {
            add(ReminderGroup(DueBucket.Overdue, "OVERDUE · ${overdue.size}", overdue.sortedBy(ReminderSummary::dueAt)))
        }
        if (upcoming.isNotEmpty()) {
            add(ReminderGroup(DueBucket.ThisWeek, "THIS WEEK · ${upcoming.size}", upcoming.sortedBy(ReminderSummary::dueAt)))
        }
    }
}

fun isOverdue(dueAt: Instant, now: Instant): Boolean = startOfDay(dueAt) < startOfDay(now)

/**
 * Distinguishes "no data yet" (truly no open reminders assigned to anyone -- the celebratory
 * all-done state) from "no results for this filter" (some other filter would show rows) --
 * telling a user with 200 reminders "nothing here" reads very differently from telling
 * everyone "you're caught up."
 */
enum class ReminderEmptyKind { None, AllDone, NoFilterMatches }

fun reminderEmptyKind(filter: com.dotdrives.model.ReminderFilter, remindersEmpty: Boolean): ReminderEmptyKind =
    when {
        !remindersEmpty -> ReminderEmptyKind.None
        filter == com.dotdrives.model.ReminderFilter.All -> ReminderEmptyKind.AllDone
        else -> ReminderEmptyKind.NoFilterMatches
    }

/** Per-row due label: "DUE 3D AGO" / "DUE YESTERDAY" / "TODAY" / "THU" / "SEP 12". */
fun dueLabel(dueAt: Instant, now: Instant): String {
    val daysDiff = daysBetween(startOfDay(now), startOfDay(dueAt))
    return when {
        daysDiff < -1 -> "DUE ${-daysDiff}D AGO"
        daysDiff == -1L -> "DUE YESTERDAY"
        daysDiff == 0L -> "TODAY"
        daysDiff in 1..6 -> weekdayLabel(dueAt)
        else -> monthDayLabel(dueAt)
    }
}

fun isDueToneUrgent(dueAt: Instant, now: Instant): Boolean = isOverdue(dueAt, now)

/** The "⚡ AUTO" flag and its context line -- never rendered when `ruleName` is unknown. */
fun autoContextLabel(ruleName: String?): String? = ruleName?.let { "⚡ $it" }

fun recurrenceLabel(recurrence: String?): String? =
    recurrence?.replaceFirstChar(Char::uppercase)

/**
 * "Would have fired N times in the last 30 days." -- the rule builder's dry-run preview.
 * `complete = false` means the count could not be fully verified (RLS hid some of the
 * matching donation rows from this viewer) -- rendered as an honest "can't verify," never as
 * a confident zero.
 */
fun fireCountLabel(count: Int?, loading: Boolean, complete: Boolean = true): String = when {
    loading -> "Checking the last 30 days…"
    count == null -> "Pick what happens, and we'll check the last 30 days."
    !complete -> "Can't verify — you may not have permission to see gift amounts."
    count == 0 -> "Hasn't fired in the last 30 days."
    count == 1 -> "Would have fired 1 time in the last 30 days."
    else -> "Would have fired $count times in the last 30 days."
}

/** A rule with a null `trigger_verb` is cron-scanned, not broken -- render a neutral label,
 * never an error state (docs/schema.md). */
fun triggerSummary(triggerVerb: String?): String = triggerVerb?.let(::verbCopy) ?: "Checked daily · no single trigger"

private fun verbCopy(verb: String): String = when (verb) {
    "gift.recorded" -> "A gift is received"
    else -> verb
}

fun ruleIcon(name: String): String {
    val normalized = name.lowercase()
    return when {
        "thank" in normalized -> "💛"
        "stale" in normalized || "nudge" in normalized -> "⏳"
        "lapsed" in normalized -> "📉"
        "pledge" in normalized || "receipt" in normalized -> "🧾"
        else -> "⚡"
    }
}

fun ruleActionSummary(rule: AutomationRule): String {
    val assignee = rule.actionConfig["assignee"]?.jsonPrimitive?.contentOrNull ?: "steward"
    val dueInDays = rule.actionConfig["due_in_days"]?.jsonPrimitive?.intOrNull
    val duePart = if (dueInDays != null) "due in ${dueInDays}d" else "due soon"
    return "${triggerSummary(rule.triggerVerb)} → step for the $assignee, $duePart"
}

fun healthRatio(daysInPhase: Long, medianDays: Double): Double =
    if (medianDays <= 0.0) 0.0 else daysInPhase / medianDays

fun healthBarPercent(daysInPhase: Long, medianDays: Double): Int =
    ((healthRatio(daysInPhase, medianDays) / 3.0) * 100).toInt().coerceIn(0, 100)

fun healthTone(daysInPhase: Long, medianDays: Double): DotTone =
    if (healthRatio(daysInPhase, medianDays) >= 3.0) DotTone.Danger else DotTone.Primary

fun healthMetaLabel(entry: DriveHealthEntry): String =
    "${entry.daysInPhase}d in ${entry.phaseName} · median ${entry.medianDays.toInt()}d"

// The money-token guard lives in shared/model (com.dotdrives.model.containsMoneyToken) and is
// enforced by the controller's draft setter and the client's write-boundary backstop -- both
// tested there directly. No copy of it lives in this view-only module.

fun formatMinAmount(cents: Long?): String =
    if (cents == null) "" else (cents / 100).toString()

fun parseMinAmountCents(text: String): Long? =
    text.trim().takeIf(String::isNotEmpty)?.toLongOrNull()?.times(100)

/** The snooze popover's three fixed offers (`portal-next-steps.dc.html`) as concrete due
 * dates, relative to `now`. "Pick a date…" has no fixed offset -- the view collects that one
 * directly from a date input. */
fun snoozeLaterToday(now: Instant): Instant = startOfDay(now).plusHours(16)

fun snoozeTomorrowMorning(now: Instant): Instant = startOfDay(now).plusHours(24 + 9)

fun snoozeNextMonday(now: Instant): Instant {
    val epochDay = now.epochSeconds.floorDiv(SECONDS_PER_DAY)
    val weekdayIndex = ((epochDay % 7) + 7) % 7 // WEEKDAY_LABELS index: THU=0 .. WED=6
    val daysUntilMonday = ((MONDAY_INDEX - weekdayIndex + 7) % 7).let { if (it == 0L) 7L else it }
    return startOfDay(now).plusDays(daysUntilMonday).plusHours(9)
}

private fun Instant.plusHours(hours: Long): Instant = Instant.fromEpochSeconds(epochSeconds + hours * 3_600L)

private fun Instant.plusDays(days: Long): Instant = Instant.fromEpochSeconds(epochSeconds + days * SECONDS_PER_DAY)

private const val MONDAY_INDEX = 4L

private fun startOfDay(instant: Instant): Instant {
    val epochDay = instant.epochSeconds.floorDiv(SECONDS_PER_DAY)
    return Instant.fromEpochSeconds(epochDay * SECONDS_PER_DAY)
}

private fun daysBetween(from: Instant, to: Instant): Long =
    (to.epochSeconds - from.epochSeconds) / SECONDS_PER_DAY

private val WEEKDAY_LABELS = listOf("THU", "FRI", "SAT", "SUN", "MON", "TUE", "WED")

/** 1970-01-01 (epoch day 0) was a Thursday -- index into [WEEKDAY_LABELS] from there. No
 * datetime library is available to `webApp` (`kotlinx-datetime` is `implementation`-scoped
 * inside `:shared` and not exposed transitively), so weekday/month math is done by hand here,
 * matching `DrivesPresentation.kt`'s existing zero-dependency style. */
private fun weekdayLabel(instant: Instant): String {
    val epochDay = instant.epochSeconds.floorDiv(SECONDS_PER_DAY)
    val index = ((epochDay % 7) + 7) % 7
    return WEEKDAY_LABELS[index.toInt()]
}

private val MONTH_ABBR = listOf("JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC")

private fun monthDayLabel(instant: Instant): String {
    val epochDay = instant.epochSeconds.floorDiv(SECONDS_PER_DAY)
    val civil = civilFromEpochDay(epochDay)
    return "${MONTH_ABBR[civil.month - 1]} ${civil.day}"
}

private data class CivilDate(val year: Long, val month: Int, val day: Int)

/** Howard Hinnant's days-from-civil / civil-from-days algorithm, integer arithmetic only. */
private fun civilFromEpochDay(epochDay: Long): CivilDate {
    val z = epochDay + 719_468L
    val era = (if (z >= 0) z else z - 146_096L) / 146_097L
    val doe = z - era * 146_097L
    val yoe = (doe - doe / 1460 + doe / 36_524 - doe / 146_096) / 365
    val y = yoe + era * 400
    val doy = doe - (365 * yoe + yoe / 4 - yoe / 100)
    val mp = (5 * doy + 2) / 153
    val d = doy - (153 * mp + 2) / 5 + 1
    val m = if (mp < 10) mp + 3 else mp - 9
    val year = if (m <= 2) y + 1 else y
    return CivilDate(year, m.toInt(), d.toInt())
}

private const val SECONDS_PER_DAY = 86_400L
